import React, { useState, useEffect } from 'react';
import { Clock, Users, Flame } from 'lucide-react';

function getTimeUntilMidnight() {
  const now = new Date();
  const midnight = new Date();
  midnight.setHours(23, 59, 59, 999);
  const diff = midnight - now;
  return {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

function getSlotsForToday() {
  const day = new Date().getDay();
  const base = [5, 4, 6, 3, 5, 7, 4];
  return base[day];
}

export default function SlotCountdown() {
  const [time, setTime] = useState(getTimeUntilMidnight());
  const totalSlots = getSlotsForToday();
  const bookedSlots = Math.max(0, totalSlots - Math.floor(time.hours / 6) - 1);
  const remaining = totalSlots - bookedSlots;

  useEffect(() => {
    const interval = setInterval(() => setTime(getTimeUntilMidnight()), 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="relative overflow-hidden py-6" data-testid="slot-countdown-section">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #5a1a2e 0%, #3d101e 50%, #1a0710 100%)' }} />
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,215,0,0.06) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

      <div className="container-custom relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
          {/* Urgency Badge */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center border border-gold/20 animate-pulse">
              <Flame className="w-5 h-5 text-gold" />
            </div>
            <div>
              <p className="text-gold font-semibold text-sm tracking-wide uppercase">Today's Slots / आज के स्लॉट</p>
              <p className="text-white/60 text-xs">Limited availability | सीमित उपलब्धता</p>
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gold/70" />
            <div className="flex gap-1.5">
              {[
                { val: pad(time.hours), label: 'Hrs' },
                { val: pad(time.minutes), label: 'Min' },
                { val: pad(time.seconds), label: 'Sec' },
              ].map((item, i) => (
                <React.Fragment key={item.label}>
                  <div className="flex flex-col items-center">
                    <span className="text-2xl md:text-3xl font-bold text-white font-heading tabular-nums leading-none" data-testid={`countdown-${item.label.toLowerCase()}`}>
                      {item.val}
                    </span>
                    <span className="text-[10px] text-gold/50 uppercase tracking-wider mt-1">{item.label}</span>
                  </div>
                  {i < 2 && <span className="text-2xl md:text-3xl text-gold/40 font-light leading-none">:</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Slots Remaining */}
          <div className="flex items-center gap-3 px-5 py-2.5 rounded-full border border-gold/20 bg-gold/5">
            <Users className="w-5 h-5 text-gold" />
            <div>
              <p className="text-white font-bold text-lg leading-tight" data-testid="slots-remaining">{remaining}</p>
              <p className="text-gold/60 text-[10px] uppercase tracking-wider">Slots Left</p>
            </div>
            <div className="flex gap-0.5 ml-1">
              {Array.from({ length: totalSlots }).map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${i < bookedSlots ? 'bg-red-400/60' : 'bg-gold/70'}`}
                />
              ))}
            </div>
          </div>

          {/* CTA */}
          <a
            href="#book-consultation"
            className="btn-gold text-sm whitespace-nowrap"
            data-testid="countdown-book-btn"
          >
            Book Now / अभी बुक करें
          </a>
        </div>
      </div>
    </section>
  );
}
